/**
 * The hand fan.
 *
 * ## The step is measured, the arc is borrowed
 *
 * Step = min(cardW − 8, (avail − cardW)/(n − 1)) — measured, not guessed
 * (FINDINGS): ten cards at 60 px land on a ~35 px step, which keeps each card's
 * vertical index visible. Thirteen cards would drop below a tap target, so the
 * kitty exchange spreads into two rows instead of shrinking the step. That
 * constraint is a tap-target result and is not up for renegotiation, so the
 * step stays exactly as it was.
 *
 * What is new is everything the step does not say. Trickster lays a hand out by
 * rotating each card about a pivot far below it — `translateY(R) rotate(θ)
 * translateY(-R)` with R = 10 card-heights — so the hand is a shallow arc and
 * each card is tilted a degree or two off its neighbour. The angle per card is
 * not fixed: it is `delta × clamp(FLEX_BASE / n, 1, FLEX_MAX)`, which opens the
 * fan out when you are holding four cards and closes it when you are holding
 * thirteen, so a hand looks held rather than dealt into a rack.
 *
 * We take the arc and the flex and keep our own step, which is the same thing
 * expressed the other way round: place each card at its measured x, then raise
 * it by R(1 − cos θ) and tilt it by θ. Same shape, and the tap targets survive.
 *
 * ## Position is a transform, so the hand can move
 *
 * Cards used to be placed with `left`, which cannot be transitioned cheaply and
 * — because they were keyed by index — remounted whenever the hand was re-sorted
 * or a card left it. Both are fixed here: a card is keyed by the card, and its
 * place is a transform. Play the ♠4 and the rest of the hand *closes the gap*
 * rather than jumping; toggle Sort and the hand rearranges in front of you.
 * That one change is most of what makes Trickster's table feel alive.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { CardId } from '@cards/shared'
import { CardFace } from './Card'
import { noteOrigin } from './flight'
import { useHandFlick } from './touch'
import { prefersReducedMotion, useSettings } from '../settings/settings'

export interface FanCard {
  card: CardId
  key?: string
  state?: 'legal' | 'illegal' | 'selected' | 'rest' | 'waiting'
  /** Tapped and on its way to the table: it rises out of the fan at once,
   *  rather than sitting still for the round trip to the server. */
  launching?: boolean
  trump?: boolean
  stamp?: string | null
  fromKitty?: boolean
  /** Coach mode's pick: ringed and tagged, a suggestion and never a play. */
  coach?: boolean
  onClick?: () => void
  disabled?: boolean
}

/** Trickster's fan constants, restated for a 2:3 card at phone scale.
 *  `FLEX` is the one that matters: the angular step is widest for a small hand
 *  and never tighter than `DELTA` for a large one. */
const FAN_DELTA_ANGLE = 1.0
const FAN_FLEX_BASE = 16
const FAN_FLEX_MAX = 2
/** Pivot distance, in card heights. Large radius, shallow arc: at ten cards the
 *  end of the fan rises about 7 % of a card. */
const FAN_RADIUS_CARDS = 10

const RAD = Math.PI / 180

/** How long the whole deal occupies the fan. It has to outlast both passes —
 *  ten cards at `--dur-deal-step` plus a `--dur-deal-card` flight, then the flip
 *  ripple at `--dur-deal-flip` a card — which comes to about 1.4 s for a hand of
 *  ten, with a little over for a slow first paint. The class is dropped
 *  afterwards so that a later re-render cannot restart the deal, and so that
 *  nothing is left carrying `dealing`'s rules once the hand is a hand. */
const DEAL_WINDOW_MS = 1600

export function Fan({
  cards,
  pad = 0,
  className,
  dealKey = null,
  onLeave,
}: {
  cards: readonly FanCard[]
  pad?: number
  className?: string
  /** Deal the hand in, once per deal. The key names the deal — any string
   *  that is new for a new deal and the same for the rest of it — so the hand
   *  is dealt when it is dealt and not whenever a fan happens to mount. Absent
   *  for the reference fans (the kitty spread, the last trick). */
  dealKey?: string | null
  /** Report each card's last place on screen as it leaves, so the table can
   *  throw it in from there (flight.ts). Only the hand you play from wants it. */
  onLeave?: boolean
}): JSX.Element {
  const host = useRef<HTMLDivElement | null>(null)
  // What the page has to be asked: the card width and the room for the fan.
  // Everything else is arithmetic on the hand, done in render — so a card
  // leaving the hand is one render and one style pass, where measuring on
  // every change of `n` forced a synchronous restyle of the whole table from
  // inside the commit and then rendered the fan a second time with the answer.
  const [box, setBox] = useState({ cardW: 0, width: 0 })
  const [settings] = useSettings()
  const n = cards.length

  const measure = useCallback(() => {
    const el = host.current
    if (!el) return
    // --card-w inherits, so a container (the kitty spread) can size its own row.
    const cardW = parseFloat(getComputedStyle(el).getPropertyValue('--card-w')) || 60
    const width = el.clientWidth
    setBox((b) => (b.cardW === cardW && b.width === width ? b : { cardW, width }))
  }, [])

  useLayoutEffect(measure, [measure, settings.cardScale])

  useEffect(() => {
    const el = host.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [measure])

  const geom = fanGeometry(box.cardW, box.width, n, pad)

  const dealing = useDeal(dealKey, n > 0)
  const leave = useLeaveRefs(onLeave === true)
  // Only the hand you play from can be picked up: it is the one whose cards
  // fly to the table, and the one whose `onClick` means "play this".
  useHandFlick(host, onLeave === true && settings.flick, (key) => {
    const c = cards.find((c) => (c.key ?? c.card) === key)
    if (!c) return undefined
    return { launching: c.launching, onThrow: c.onClick && !c.disabled ? c.onClick : undefined }
  })

  // Flex opens the fan for a short hand and closes it for a long one; a hand of
  // one is flat, because there is no neighbour to be at an angle to.
  const flex = Math.min(Math.max(FAN_FLEX_BASE / Math.max(n, 1), 1), FAN_FLEX_MAX)
  const delta = n > 1 ? FAN_DELTA_ANGLE * flex : 0
  const radius = geom.cardH * FAN_RADIUS_CARDS

  return (
    <div
      className={[dealing ? 'hand dealing' : 'hand', onLeave && settings.flick ? 'flick' : '', className].filter(Boolean).join(' ')}
      ref={host}
      style={{ '--deal-n': n } as CSSProperties}
    >
      {/* Nothing until the fan has measured itself. A card's place is a
          transitioned transform, and `measure` reads computed style, which
          forces the browser to resolve a freshly mounted card at x = 0 —
          so every new fan (each change of screen) used to paint squashed
          against the left edge and slide open. Rendered only once the step
          is known, a card is inserted already where it goes, and a card
          that is inserted has nothing to transition from. The measure runs
          in a layout effect, so this costs no frame. */}
      {geom.cardH === 0 ? null : cards.map((c, i) => {
        const angle = (i - (n - 1) / 2) * delta
        const x = geom.x0 + i * geom.step
        // Rotating about a pivot `radius` below the hand lifts the ends of the
        // fan; the sideways part of that rotation is already in the step.
        const lift = radius * (1 - Math.cos(angle * RAD))
        return (
          <CardFace
            key={c.key ?? c.card}
            cardKey={c.key ?? c.card}
            rootRef={leave(c.card, angle)}
            card={c.card}
            deck={settings.deck}
            state={c.state}
            launching={c.launching}
            trump={c.trump}
            stamp={c.stamp}
            fromKitty={c.fromKitty}
            className={c.coach ? 'coach' : undefined}
            flip={dealing}
            onClick={c.onClick}
            disabled={c.disabled}
            control={onLeave === true}
            style={
              {
                // Later cards sit on top, so the fan reads left to right.
                zIndex: i + 1,
                transform: `translate(${x.toFixed(2)}px, ${(-lift).toFixed(2)}px) rotate(${angle.toFixed(2)}deg)`,
                '--deal-i': i,
                // Where this card comes from: the middle of the band, which is
                // under the deck on the felt above it.
                '--deal-x': `${(geom.mid - x).toFixed(2)}px`,
              } as CSSProperties
            }
          />
        )
      })}
    </div>
  )
}

/** Where the cards go: the measured step, the start of the row, and the
 *  middle the deal flies in from. Zero card height means not yet measured.
 *
 *  Height is not read from `--card-h`: an unregistered custom property
 *  computes to the unresolved `calc(60px * 1.5)`, which `parseFloat` reads as
 *  60. The assets are 2:3 and the token says so, so the ratio is the honest
 *  source. */
function fanGeometry(cardW: number, width: number, n: number, pad: number): { step: number; x0: number; cardH: number; mid: number } {
  if (cardW === 0) return { step: 0, x0: 0, cardH: 0, mid: 0 }
  const avail = width - pad * 2
  const step = n > 1 ? Math.min(cardW - 8, (avail - cardW) / (n - 1)) : 0
  const x0 = pad + (avail - (cardW + step * (n - 1))) / 2
  return { step, x0, cardH: cardW * 1.5, mid: pad + (avail - cardW) / 2 }
}

/** When each deal started, by its key. Module state rather than component
 *  state, because the thing that went wrong was the component: the auction and
 *  the play are different screens, each with its own hand band, so the fan was
 *  remounted the moment the bidding closed — and a fan that mounts holding ten
 *  cards could not tell that from ten cards arriving, so it dealt them again.
 *  One entry is enough: only the deal in front of you can still be animating. */
const dealtAt = new Map<string, number>()

/** True for as long as the deal named by `key` is still flying in.
 *
 *  A deal plays once, from when its hand first appears, however many fans show
 *  it. A fan that mounts part-way through a deal picks it up at the same
 *  point rather than starting it again. Reduced motion skips the window
 *  entirely rather than relying on the zeroed duration tokens, so nothing is
 *  ever mid-flight when a player has asked for stillness. */
function useDeal(key: string | null, hasCards: boolean): boolean {
  const [dealing, setDealing] = useState(false)
  useEffect(() => {
    if (key === null || !hasCards || prefersReducedMotion()) {
      setDealing(false)
      return
    }
    let start = dealtAt.get(key)
    if (start === undefined) {
      start = Date.now()
      dealtAt.clear()
      dealtAt.set(key, start)
    }
    const left = start + DEAL_WINDOW_MS - Date.now()
    if (left <= 0) {
      setDealing(false)
      return
    }
    setDealing(true)
    const timer = window.setTimeout(() => setDealing(false), left)
    return () => window.clearTimeout(timer)
  }, [key, hasCards])
  return dealing
}

/** A stable ref per card that notes where the card was when it detaches.
 *
 *  React detaches a removed element's ref before it takes the element out of
 *  the document, so at that moment the card is still exactly where the player
 *  last saw it. The callbacks are cached per card because a new function each
 *  render would detach and re-attach every card on every snapshot, measuring
 *  all ten each time. The angle is read from a ref so the cached callback
 *  always has the current one. */
function useLeaveRefs(enabled: boolean): (card: CardId, angle: number) => ((el: HTMLElement | null) => void) | undefined {
  const refs = useRef(new Map<CardId, (el: HTMLElement | null) => void>())
  const angles = useRef(new Map<CardId, number>())
  return (card, angle) => {
    if (!enabled) return undefined
    angles.current.set(card, angle)
    let ref = refs.current.get(card)
    if (!ref) {
      let node: HTMLElement | null = null
      ref = (el) => {
        if (el) {
          node = el
          return
        }
        if (node) noteOrigin(card, node, angles.current.get(card) ?? 0)
        node = null
        refs.current.delete(card)
        angles.current.delete(card)
      }
      refs.current.set(card, ref)
    }
    return ref
  }
}
